// Header streak-nudge ladder: 4 time-of-day slots (UTC-hour buckets, same
// UTC-midnight/05:30 IST day-rollover convention as engagementPresentation's
// AT_RISK_UTC_HOUR), each shown at most once per streak-day. Shared by the
// mobile and web header Streak components — keep this file identical across
// both repos (only @/Utils/CommonUtils's underlying storage impl differs:
// AsyncStorage/MMKV on native, localForage on web).

import {getLocalStorage, setLocalStorage} from '@/Utils/CommonUtils';

export type NudgeSlot = 'fresh' | 'midday' | 'evening' | 'lastcall';

const STORAGE_KEY_PREFIX = 'streak_nudges_shown:';

// slot = f(now.getUTCHours()) — 'lastcall' [18..23] aligns with
// engagementPresentation.ts's AT_RISK_UTC_HOUR=18 evening at-risk banner.
export function getNudgeSlot(nowUtcHour: number): NudgeSlot {
  if (nowUtcHour < 6) {
    return 'fresh';
  }
  if (nowUtcHour < 12) {
    return 'midday';
  }
  if (nowUtcHour < 18) {
    return 'evening';
  }
  return 'lastcall';
}

export function buildNudgeCopy(
  slot: NudgeSlot,
  streakCount: number,
  nextShort?: string | null,
): string {
  const toward = nextShort ? ` toward the ${nextShort}` : '';
  switch (slot) {
    case 'fresh':
      return streakCount > 0
        ? `Day ${streakCount + 1} awaits — win a game to keep your streak going`
        : `Win a game today to start your streak${toward}`;
    case 'midday':
      return streakCount > 0
        ? `Still time today — win 1 game to keep your ${streakCount}-day streak`
        : `A single win today starts your streak${toward}`;
    case 'evening':
      return streakCount > 0
        ? `Your ${streakCount}-day streak is on the line — win a game today`
        : 'Last stretch of the day — win 1 game to light your streak';
    case 'lastcall':
    default:
      return streakCount > 0
        ? `⏳ Your ${streakCount}-day streak is about to break — win now!`
        : "⏳ Day's almost over — one win starts your streak";
  }
}

export function shouldShowNudge(opts: {
  slot: NudgeSlot;
  shownSlots: NudgeSlot[];
  pageEnabled: boolean;
  todayComplete: boolean;
}): boolean {
  const {slot, shownSlots, pageEnabled, todayComplete} = opts;
  if (!pageEnabled || todayComplete) {
    return false;
  }
  return !shownSlots.includes(slot);
}

// Storage key buckets by the summary's server date (UTC-midnight day
// rollover) so a later slot never back-fills an earlier one and yesterday's
// shown-state doesn't suppress today's ladder. Falls back to the client's
// UTC date string if no serverDate is available yet (e.g. before the
// engagement summary has loaded).
function todayKey(serverDate?: string): string {
  const parsed = serverDate ? new Date(serverDate) : null;
  const date = parsed && !isNaN(parsed.getTime()) ? parsed : new Date();
  return date.toISOString().slice(0, 10);
}

export function nudgeStorageKey(serverDate?: string): string {
  return `${STORAGE_KEY_PREFIX}${todayKey(serverDate)}`;
}

// Storage read/write are wrapped defensively (web's getLocalStorage/
// setLocalStorage don't try/catch internally, unlike native's — a blocked/
// unavailable localForage would otherwise surface as an unhandled rejection
// out of the header on every page) — fail silently to "nothing shown yet" /
// "didn't persist" rather than throwing, since a missed nudge is harmless.
export async function getShownSlots(serverDate?: string): Promise<NudgeSlot[]> {
  try {
    const raw = await getLocalStorage(nudgeStorageKey(serverDate));
    return Array.isArray(raw) ? (raw as NudgeSlot[]) : [];
  } catch {
    return [];
  }
}

// Old-key cleanup is intentionally a no-op (per-day keys are cheap and fine
// to let linger) — only today's key is ever read/written.
export async function markNudgeShown(
  slot: NudgeSlot,
  serverDate?: string,
): Promise<void> {
  try {
    const shown = await getShownSlots(serverDate);
    if (shown.includes(slot)) {
      return;
    }
    await setLocalStorage(nudgeStorageKey(serverDate), [...shown, slot]);
  } catch {
    // no-op — see getShownSlots.
  }
}
